import React from "react";
import { 
  ScanLine, 
  Flame, 
  CheckCircle2, 
  TrendingUp, 
  Home, 
  Layers, 
  User,
  Download
} from "lucide-react";

export default function ExpoLeadHandheldMockup() {
  return (
    <div className="relative flex items-center justify-center p-4 sm:p-8 min-h-[600px] select-none">
      
      {/* AMBIENT STUDIO GLOW */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[380px] sm:w-[460px] h-[380px] sm:h-[460px] bg-gradient-to-tr from-blue-600/20 via-indigo-500/15 to-emerald-400/15 rounded-full blur-[100px] pointer-events-none" />

      {/* 3D TILT DEVICE WRAPPER */}
      <div className="relative z-10 transform -rotate-6 hover:rotate-0 transition-transform duration-500 ease-out">
        
        {/* TITANIUM PHONE CHASSIS */}
        <div className="relative w-[285px] sm:w-[315px] h-[610px] sm:h-[640px] bg-slate-950 rounded-[50px] p-3.5 shadow-[0_30px_70px_-15px_rgba(15,23,42,0.45)] border-[5px] border-slate-300/90 ring-1 ring-slate-900/10">
          
          {/* IPHONE SIDE BUTTONS */}
          <div className="absolute -left-[8px] top-[75px] w-[3px] h-5 bg-slate-400 rounded-l-md shadow-sm" />
          <div className="absolute -left-[8px] top-[115px] w-[3px] h-11 bg-slate-400 rounded-l-md shadow-sm" />
          <div className="absolute -left-[8px] top-[170px] w-[3px] h-11 bg-slate-400 rounded-l-md shadow-sm" />
          <div className="absolute -right-[8px] top-[135px] w-[3px] h-14 bg-slate-400 rounded-r-md shadow-sm" />

          {/* DISPLAY GLASS CONTAINER */}
          <div className="relative w-full h-full bg-slate-50 rounded-[38px] overflow-hidden flex flex-col justify-between font-sans text-slate-800 border border-slate-200/80 shadow-inner">
            
            {/* DYNAMIC ISLAND */}
            <div className="absolute top-2.5 left-1/2 -translate-x-1/2 w-24 h-6 bg-slate-950 rounded-full z-50 flex items-center justify-end px-2.5 gap-1.5 shadow-sm">
              <span className="w-2.5 h-2.5 rounded-full bg-indigo-950 border border-indigo-500/30" />
              <span className="w-2 h-2 rounded-full bg-blue-900/50" />
            </div>

            {/* STATUS BAR */}
            <div className="flex justify-between items-center px-6 pt-3 text-[11px] font-bold text-slate-900 z-40">
              <span>9:41</span>
              <div className="flex items-center gap-1.5 text-[10px]">
                <span>5G</span>
                <div className="w-5 h-2.5 border border-slate-900 rounded-sm p-0.5 flex items-center">
                  <div className="w-full h-full bg-slate-900 rounded-xs" />
                </div>
              </div>
            </div>

            {/* SCROLLABLE CONTENT */}
            <div className="px-4 pt-2.5 pb-2 space-y-2.5 overflow-y-auto no-scrollbar flex-1">
              
              {/* App Header */}
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-1 text-[10px] font-black text-blue-600 tracking-wider uppercase">
                    <ScanLine className="w-3 h-3" /> EXPOLEAD
                  </div>
                  <h3 className="text-lg font-black text-slate-900 leading-tight">Dashboard</h3>
                  <p className="text-[9px] text-slate-400 font-medium">Auto-Sync • Trade Show Mode</p>
                </div>
                <div className="relative">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-tr from-blue-600 to-indigo-600 text-white flex items-center justify-center font-extrabold text-xs shadow-md shadow-blue-500/20">
                    KS
                  </div>
                  <span className="absolute top-0 right-0 w-2.5 h-2.5 bg-emerald-500 border-2 border-white rounded-full" />
                </div>
              </div>

              {/* WIDGET 1: CARDS PROCESSED (FIXED STRICT SVG SIZING) */}
              <div className="bg-white p-3 rounded-2xl border border-slate-100 shadow-sm flex items-center justify-between gap-3 overflow-hidden">
                
                {/* DONUT CHART CONTAINER (STRICT w-14 h-14 TO PREVENT OVERFLOW) */}
                <div className="relative w-14 h-14 flex-shrink-0 flex items-center justify-center">
                  <svg className="w-14 h-14 transform -rotate-90" viewBox="0 0 36 36">
                    <path
                      className="text-slate-100"
                      strokeWidth="4"
                      stroke="currentColor"
                      fill="none"
                      d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                    />
                    <path
                      className="text-blue-600"
                      strokeDasharray="85, 100"
                      strokeWidth="4"
                      strokeLinecap="round"
                      stroke="currentColor"
                      fill="none"
                      d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                    />
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
                    <span className="text-xs font-black text-slate-900 leading-none">85%</span>
                    <span className="text-[8px] font-bold text-slate-400">Target</span>
                  </div>
                </div>

                <div className="flex-1 space-y-1">
                  <span className="text-[9px] font-bold uppercase tracking-wider text-slate-400 block">Cards Processed</span>
                  <div className="flex items-baseline gap-1">
                    <span className="text-lg font-black text-slate-900">142</span>
                    <span className="text-xs font-semibold text-slate-400">/ 180</span>
                  </div>
                  <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[8px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
                    <CheckCircle2 className="w-2.5 h-2.5 text-emerald-600" /> WhatsApp Auto-Sent
                  </div>
                </div>
              </div>

              {/* WIDGET 2: HOT LEADS & CLOUD SYNC */}
              <div className="grid grid-cols-2 gap-2">
                <div className="bg-gradient-to-br from-rose-50 to-orange-50/50 p-2.5 rounded-2xl border border-rose-100 shadow-sm">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[8px] font-bold uppercase tracking-wider text-rose-600">Hot Leads</span>
                    <Flame className="w-3.5 h-3.5 text-rose-500 fill-rose-500" />
                  </div>
                  <div className="text-lg font-black text-slate-900">54</div>
                  <p className="text-[8px] font-medium text-slate-500">High Conversion</p>
                </div>

                <div className="bg-gradient-to-br from-blue-50 to-indigo-50/50 p-2.5 rounded-2xl border border-blue-100 shadow-sm">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[8px] font-bold uppercase tracking-wider text-blue-600">FLow-Up</span>
                    <TrendingUp className="w-3.5 h-3.5 text-blue-600" />
                  </div>
                  <div className="text-lg font-black text-slate-900">25</div>
                  <p className="text-[8px] font-medium text-slate-500"></p>
                </div>
              </div>

              {/* WIDGET 3: SCAN ACTIVITY TREND */}
              <div className="bg-white p-2.5 rounded-2xl border border-slate-100 shadow-sm space-y-1">
                <div className="flex items-center justify-between">
                  <span className="text-[8px] font-bold uppercase tracking-wider text-slate-400">Scan Activity Trend</span>
                  <span className="text-[8px] font-bold text-emerald-600">+24% vs Day 1</span>
                </div>
                
                <div className="h-9 w-full flex items-end gap-1.5 pt-1">
                  <div className="w-1/5 h-[30%] bg-blue-100 rounded-t-sm" />
                  <div className="w-1/5 h-[55%] bg-blue-200 rounded-t-sm" />
                  <div className="w-1/5 h-[40%] bg-blue-300 rounded-t-sm" />
                  <div className="w-1/5 h-[80%] bg-blue-500 rounded-t-sm" />
                  <div className="w-1/5 h-[100%] bg-gradient-to-t from-blue-600 to-indigo-600 rounded-t-sm" />
                </div>
              </div>

              {/* APP DOWNLOAD CARDS (ANDROID + IPHONE) */}
              <div className="space-y-1.5 pt-0.5">
                
                {/* Android App */}
                <div className="bg-[#000] p-2 rounded-xl text-white shadow-md flex items-center justify-between border border-slate-800">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-lg bg-emerald-500/20 text-emerald-400 flex items-center justify-center flex-shrink-0">
                      <svg className="w-3.5 h-3.5 fill-current text-emerald-400" viewBox="0 0 24 24">
                        <path d="M3.609 1.814L13.792 12 3.61 22.186a1.996 1.996 0 0 1-.61-.92V2.734a1.996 1.996 0 0 1 .609-.92zm11.248 11.248l2.298 2.298-10.937 6.313 8.639-8.611zm3.176-1.841l2.427 1.4a1.637 1.637 0 0 1 0 2.844l-2.43 1.402-1.578-1.578 1.581-4.068zM5.218 2.327l10.937 6.313-2.298 2.298-8.639-8.611z"/>
                      </svg>
                    </div>
                    <div>
                      <div className="text-[8px] font-black uppercase text-slate-400 leading-none">EXPOLEAD MOBILE</div>
                      <div className="text-[10px] font-bold text-white leading-tight">Android App</div>
                    </div>
                  </div>
                  <button 
                    onClick={() => window.open("https://play.google.com/store/apps/details?id=in.thensgroup.expolead", "_blank")}
                    className="px-2.5 py-1 bg-orange-600  text-white font-black text-[8px] rounded-lg shadow-sm flex items-center gap-1 transition-colors"
                  >
                    <Download className="w-2.5 h-2.5" /> Get App
                  </button>
                </div>

                {/* iPhone App */}
                <div className="bg-slate-900 p-2 rounded-xl text-white shadow-md flex items-center justify-between border border-slate-800">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-lg bg-blue-500/20 text-blue-400 flex items-center justify-center flex-shrink-0">
                      <svg className="w-3.5 h-3.5 fill-current text-blue-400" viewBox="0 0 24 24">
                        <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M15.97 6.35c.64-.78 1.08-1.86.96-2.95-.93.04-2.07.62-2.73 1.39-.58.68-1.1 1.78-.96 2.85 1.04.08 2.11-.52 2.73-1.29z"/>
                      </svg>
                    </div>
                    <div>
                      <div className="text-[8px] font-black uppercase text-slate-400 leading-none">EXPOLEAD MOBILE</div>
                      <div className="text-[10px] font-bold text-white leading-tight">iPhone App</div>
                    </div>
                  </div>
                  <button 
                    onClick={() => window.open("https://apple.com/app-store", "_blank")}
                    className="px-2.5 py-1 bg-sky-blue-600 hover:bg-indigo-500 text-white font-black text-[8px] rounded-lg shadow-sm flex items-center gap-1 transition-colors"
                  >
                    <Download className="w-2.5 h-2.5" /> Get App
                  </button>
                </div>

              </div>

            </div>

            {/* 4-ITEM BOTTOM NAVIGATION BAR */}
            <div className="bg-white/95 backdrop-blur-md px-6 py-2.5 border-t border-slate-100 flex items-center justify-around z-40">
              <button className="flex flex-col items-center gap-0.5 text-blue-600 min-w-[40px]">
                <Home className="w-4 h-4" />
                <span className="text-[9px] font-extrabold">Home</span>
              </button>

              <button className="flex flex-col items-center gap-0.5 text-slate-400 hover:text-slate-600 transition-colors min-w-[40px]">
                <Layers className="w-4 h-4" />

                <span className="text-[9px] font-bold">Leads</span>
              </button>

              <button className="w-10 h-10 -mt-6 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-full flex items-center justify-center shadow-lg shadow-blue-500/40 border-2 border-white transform hover:scale-105 active:scale-95 transition-transform">
                <ScanLine className="w-4 h-4" />
              </button>

              <button className="flex flex-col items-center gap-0.5 text-slate-400 hover:text-slate-600 transition-colors min-w-[40px]">
                <User className="w-4 h-4" />
                <span className="text-[9px] font-bold">Options</span>
              </button>
            </div>

            {/* HOME INDICATOR */}
            <div className="w-24 h-1 bg-slate-900/20 rounded-full mx-auto my-1.5" />

          </div>
        </div>

      </div>

    </div>
  );
}